import { Capacitor } from "@capacitor/core";
import {
  getNativeInternalPath,
  isVaultGridHttpsUrl,
} from "./appLinkRouting";

export { isVaultGridHttpsUrl } from "./appLinkRouting";

export const NATIVE_API_BASE_URL = String(
  process.env.REACT_APP_NATIVE_API_URL || "https://mobile-api.vaultgrid.net/api"
).replace(/\/+$/, "");

// Bootstrap responses from already released backend versions still describe
// the two legacy transport origins.
export const LEGACY_PRIMARY_NATIVE_API_BASE_URL = "https://vaultgrid.net/api";

export const RU_NATIVE_API_BASE_URL = String(
  process.env.REACT_APP_RU_NATIVE_API_URL || "https://ru.vaultgrid.net/api"
).replace(/\/+$/, "");

export const PUBLIC_WEB_ORIGIN = String(
  process.env.REACT_APP_PUBLIC_WEB_ORIGIN || "https://vaultgrid.net"
).replace(/\/+$/, "");

export const RU_PUBLIC_WEB_ORIGIN = String(
  process.env.REACT_APP_RU_PUBLIC_WEB_ORIGIN || "https://ru.vaultgrid.net"
).replace(/\/+$/, "");

export const NATIVE_TRANSPORT_CHANGED_EVENT = "vg:native-transport-changed";

let currentNativeApiBaseUrl = NATIVE_API_BASE_URL;
let currentPublicWebOrigin = PUBLIC_WEB_ORIGIN;

const isAllowedTransportPair = (apiOrigin, publicOrigin) => (
  (
    apiOrigin === NATIVE_API_BASE_URL
    && (publicOrigin === PUBLIC_WEB_ORIGIN || publicOrigin === RU_PUBLIC_WEB_ORIGIN)
  )
  || (
    apiOrigin === RU_NATIVE_API_BASE_URL
    && (publicOrigin === PUBLIC_WEB_ORIGIN || publicOrigin === RU_PUBLIC_WEB_ORIGIN)
  )
);

export const getNativeApiBaseUrl = () => currentNativeApiBaseUrl;
export const getPublicWebOrigin = () => currentPublicWebOrigin;

export function setNativeTransportOrigins({ apiOrigin, publicOrigin }) {
  const nextApiOrigin = String(apiOrigin || "").replace(/\/+$/, "");
  const nextPublicOrigin = String(publicOrigin || "").replace(/\/+$/, "");
  if (!isAllowedTransportPair(nextApiOrigin, nextPublicOrigin)) {
    throw new Error("Rejected an untrusted native transport origin.");
  }

  currentNativeApiBaseUrl = nextApiOrigin;
  currentPublicWebOrigin = nextPublicOrigin;
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(NATIVE_TRANSPORT_CHANGED_EVENT, {
      detail: { apiOrigin: nextApiOrigin, publicOrigin: nextPublicOrigin },
    }));
  }
}

export function resetNativeTransportOrigins() {
  setNativeTransportOrigins({
    apiOrigin: NATIVE_API_BASE_URL,
    publicOrigin: PUBLIC_WEB_ORIGIN,
  });
}

export const isNativeApp = () => Capacitor.isNativePlatform();

export const getNativePlatform = () => (
  isNativeApp() ? Capacitor.getPlatform() : "web"
);

export const isNativeIOS = () => getNativePlatform() === "ios";
export const isNativeAndroid = () => getNativePlatform() === "android";

export function toPublicWebUrl(path = "") {
  const value = String(path || "").trim();
  if (/^https:\/\//i.test(value)) {
    if (
      isNativeApp()
      && getPublicWebOrigin() === RU_PUBLIC_WEB_ORIGIN
      && isVaultGridHttpsUrl(value)
    ) {
      const parsed = new URL(value);
      const ruOrigin = new URL(RU_PUBLIC_WEB_ORIGIN);
      parsed.protocol = ruOrigin.protocol;
      parsed.host = ruOrigin.host;
      return parsed.toString();
    }
    return value;
  }

  const browserOrigin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : PUBLIC_WEB_ORIGIN;
  const origin = isNativeApp() ? getPublicWebOrigin() : browserOrigin;
  const normalizedPath = value.startsWith("/") ? value : `/${value}`;
  return `${origin}${normalizedPath}`;
}

const DIGITAL_PRODUCT_CATEGORIES = new Set([
  "diamonds",
  "skins",
  "subscriptions",
  "codes",
]);

export const isNativeDigitalProductCategory = (category) => (
  isNativeApp() && DIGITAL_PRODUCT_CATEGORIES.has(String(category || "").toLowerCase())
);

export const toNativeApiUrl = (path = "") => {
  const value = String(path || "").trim();
  if (/^https?:\/\//i.test(value)) return value;
  const normalizedPath = value.startsWith("/") ? value : `/${value}`;
  return `${getNativeApiBaseUrl()}${normalizedPath}`;
};

export async function openExternalUrl(rawUrl) {
  const url = String(rawUrl || "").trim();
  if (!url) return false;

  if (!isNativeApp()) {
    window.open(url, "_blank", "noopener,noreferrer");
    return true;
  }

  let protocol = "";
  try {
    protocol = new URL(url).protocol;
  } catch {
    return false;
  }

  if (!["http:", "https:"].includes(protocol)) {
    const { AppLauncher } = await import("@capacitor/app-launcher");
    await AppLauncher.openUrl({ url });
    return true;
  }

  const { Browser } = await import("@capacitor/browser");
  await Browser.open({ url });
  return true;
}

// Opens HTTPS links through the operating system instead of an in-app browser.
// On iOS and Android this gives an installed app's Universal/App Link the first
// chance to handle the URL, while the OS browser remains the natural fallback.
export async function openUrlPreferInstalledApp(rawUrl) {
  const url = String(rawUrl || "").trim();
  if (!url) return false;

  if (!isNativeApp()) {
    return openExternalUrl(url);
  }

  let protocol = "";
  try {
    protocol = new URL(url).protocol;
  } catch {
    return false;
  }
  if (!["http:", "https:"].includes(protocol)) return false;

  const { AppLauncher } = await import("@capacitor/app-launcher");
  const result = await AppLauncher.openUrl({ url });
  return Boolean(result?.completed);
}

export async function shareContent(options = {}) {
  if (isNativeApp()) {
    const { Share } = await import("@capacitor/share");
    await Share.share(options);
    return true;
  }

  if (typeof navigator !== "undefined" && navigator.share) {
    await navigator.share(options);
    return true;
  }
  return false;
}

export async function getCurrentPosition(options = {}) {
  const normalizedOptions = {
    enableHighAccuracy: Boolean(options?.enableHighAccuracy),
    timeout: Number(options?.timeout || 12000),
    maximumAge: Number(options?.maximumAge ?? 60000),
  };

  if (isNativeApp()) {
    const { Geolocation } = await import("@capacitor/geolocation");
    const permissions = await Geolocation.checkPermissions();
    if (!["granted", "limited"].includes(permissions.location)) {
      await Geolocation.requestPermissions({ permissions: ["location"] });
    }
    return Geolocation.getCurrentPosition(normalizedOptions);
  }

  if (typeof navigator === "undefined" || !navigator.geolocation) {
    const error = new Error("Geolocation is not supported");
    error.code = 2;
    throw error;
  }

  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, normalizedOptions);
  });
}

export async function getLocationPermissionState() {
  if (isNativeApp()) {
    const { Geolocation } = await import("@capacitor/geolocation");
    const permissions = await Geolocation.checkPermissions();
    return permissions.location || "prompt";
  }

  if (typeof navigator === "undefined" || !navigator.permissions?.query) return "unknown";
  try {
    const result = await navigator.permissions.query({ name: "geolocation" });
    return result?.state || "unknown";
  } catch {
    return "unknown";
  }
}

export function getInternalPathFromAppUrl(rawUrl) {
  if (isNativeApp() && isVaultGridHttpsUrl(rawUrl)) {
    const linkIsRussian = new URL(rawUrl).hostname === new URL(RU_PUBLIC_WEB_ORIGIN).hostname;
    const accountIsRussian = getPublicWebOrigin() === RU_PUBLIC_WEB_ORIGIN;
    if (linkIsRussian !== accountIsRussian) return null;
  }
  return getNativeInternalPath(rawUrl);
}
